import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { motion } from 'framer-motion';
import { Loader2 } from 'lucide-react';
import { Slot } from '@radix-ui/react-slot';
import { cn } from '@/lib/utils';

export type ButtonVariant = 'primary' | 'secondary' | 'accent' | 'ghost';
export type ButtonSize = 'sm' | 'md' | 'lg';

export interface ButtonProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  /** Button variant style */
  variant?: ButtonVariant;
  /** Button size */
  size?: ButtonSize;
  /** Loading state - shows spinner and disables button */
  isLoading?: boolean;
  /** Loading text to display */
  loadingText?: string;
  /** Icon to display before text */
  leftIcon?: ReactNode;
  /** Icon to display after text */
  rightIcon?: ReactNode;
  /** Full width button */
  fullWidth?: boolean;
  /** Render as child element (useful for Link components) */
  asChild?: boolean;
  /** Button content */
  children: ReactNode;
}

const variantClasses: Record<ButtonVariant, string> = {
  primary: cn(
    'bg-primary !text-white',
    'hover:bg-primary-light active:bg-primary-dark',
    'focus-visible:ring-primary',
    '[&>*]:text-white [&]:text-white',
    'shadow-lg shadow-primary/25 hover:shadow-primary/40'
  ),
  secondary: cn(
    'border-2 border-primary !text-primary bg-transparent',
    'hover:bg-primary hover:!text-white',
    'focus-visible:ring-primary'
  ),
  accent: cn(
    'bg-white !text-primary',
    'hover:bg-cream-100 active:bg-cream-200',
    'focus-visible:ring-primary',
    '[&>*]:text-primary [&]:text-primary'
  ),
  ghost: cn(
    '!text-primary bg-transparent',
    'hover:!text-primary-dark hover:underline',
    'focus-visible:ring-primary'
  ),
};

const sizeClasses: Record<ButtonSize, string> = {
  sm: 'py-1.5 px-3 text-caption gap-1.5',
  md: 'py-2 px-4 text-body-sm gap-2',
  lg: 'py-3 px-6 text-body gap-2',
};

/**
 * Button Component
 *
 * A versatile button component with multiple variants, sizes, and states.
 * Supports loading states, icons, asChild pattern, and full accessibility.
 *
 * @example
 * ```tsx
 * <Button variant="primary" size="lg">
 *   Book Consultation
 * </Button>
 *
 * <Button variant="secondary" isLoading loadingText="Submitting...">
 *   Submit
 * </Button>
 *
 * <Button variant="ghost" leftIcon={<ArrowLeft />}>
 *   Go Back
 * </Button>
 *
 * // Using asChild with Link
 * <Button asChild>
 *   <Link to="/contact">Contact Us</Link>
 * </Button>
 * ```
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = 'primary',
      size = 'md',
      isLoading = false,
      loadingText,
      leftIcon,
      rightIcon,
      fullWidth = false,
      asChild = false,
      disabled,
      className,
      children,
      type = 'button',
      style,
      ...props
    },
    ref
  ) => {
    const isDisabled = disabled || isLoading;

    const buttonClasses = cn(
      // Base styles
      'inline-flex items-center justify-center font-semibold rounded-lg',
      'transition-colors duration-200',
      'focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2',
      'disabled:opacity-50 disabled:cursor-not-allowed',
      // Variant styles
      variantClasses[variant],
      // Size styles
      sizeClasses[size],
      // Full width
      fullWidth && 'w-full',
      className
    );

    // Use Slot for asChild pattern (allows wrapping Link, etc.)
    if (asChild) {
      return (
        <Slot ref={ref} className={buttonClasses} {...props}>
          {children}
        </Slot>
      );
    }

    // Standard button with motion
    // Using type assertion to handle framer-motion's strict exactOptionalPropertyTypes
    const motionProps = { ...props, ...(style ? { style } : {}) } as Record<string, unknown>;

    return (
      <motion.button
        ref={ref}
        type={type}
        disabled={isDisabled}
        whileTap={{ scale: isDisabled ? 1 : 0.98 }}
        className={buttonClasses}
        {...motionProps}
      >
        {isLoading ? (
          <>
            <Loader2
              className="animate-spin"
              size={size === 'sm' ? 14 : size === 'md' ? 16 : 18}
              aria-hidden="true"
            />
            <span>{loadingText || 'Loading...'}</span>
          </>
        ) : (
          <>
            {leftIcon && (
              <span className="flex-shrink-0" aria-hidden="true">
                {leftIcon}
              </span>
            )}
            <span>{children}</span>
            {rightIcon && (
              <span className="flex-shrink-0" aria-hidden="true">
                {rightIcon}
              </span>
            )}
          </>
        )}
      </motion.button>
    );
  }
);

Button.displayName = 'Button';

export default Button;
